import { useMemo, type RefObject } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { useResizeObserver } from '@ardoq/hooks';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import type {
  FormatNodeValue,
  SizedNode,
  TreemapHierarchyNode,
  TreemapTilingMethod,
} from '../../types';
import { buildHierarchy } from '../buildHierarchy';
import { HierarchicalTreemapViewNode } from './HierarchicalTreemapViewNode';
import { HIERARCHICAL_TREEMAP_PADDING, TILING_METHODS } from './consts';
import { toGroupHeaderHeight } from './toGroupHeaderHeight';
import { t, type I18nLocale } from '@ardoq/i18n';
import { toAccessibleName, toNavigableNodes } from '../hierarchyNavigation';
import { useHierarchyNavigation } from '../useHierarchyNavigation';
import { toDrawnOrder } from '../toDrawnOrder';

type Size = [width: number, height: number];

const paddingExceptRoot = (value: number) => (node: TreemapHierarchyNode) =>
  node.parent ? value : 0;

const layout = (size: Size, tilingMethod: TreemapTilingMethod) =>
  d3
    .treemap<SizedNode>()
    .tile(TILING_METHODS[tilingMethod].method)
    .size(size)
    .paddingOuter(paddingExceptRoot(HIERARCHICAL_TREEMAP_PADDING))
    .paddingInner(HIERARCHICAL_TREEMAP_PADDING)
    .paddingTop(toGroupHeaderHeight);

const TreemapContainer = styled(VisualizationContainer)`
  position: relative;
  overflow: hidden;
`;

export type HierarchicalTreemapVisualizationProps = {
  sizedNodes: SizedNode[];
  tilingMethod: TreemapTilingMethod;
  maxDepth: number;
  formatValue: FormatNodeValue;
  viewContainer: RefObject<HTMLDivElement | null>;
  i18nLocale: I18nLocale;
};

/**
 * The treemap itself: a laid-out hierarchy and nothing about which mode is drawing it.
 *
 * Split out from the view so the workspace-mode and viewpoint-mode views can each wrap it in their
 * own shell without one of them having to know the other exists. It takes no view id, no settings
 * object and no `isViewpointMode`; what it needs is a tree, two numbers and a formatter.
 *
 * Shared between the two modes only because nothing has diverged yet. When the viewpoint version
 * is restyled the way Component Matrix 2 was, it swaps this for its own and workspace mode is
 * untouched — which is the property the fork buys, rather than the duplication itself.
 */
export const HierarchicalTreemapVisualization = ({
  sizedNodes,
  tilingMethod,
  maxDepth,
  formatValue,
  viewContainer,
  i18nLocale,
}: HierarchicalTreemapVisualizationProps) => {
  const { width = 0, height = 0 } = useResizeObserver(viewContainer);
  /* One memo over the build and the layout together, rather than a laid-out tree from the stream.
     `useResizeObserver` fires per frame of a splitter drag and `d3.treemap` is not cheap; keeping
     the build inside it is what makes the mutation safe, since the memo then owns the nodes it
     writes geometry onto. `maxDepth` is deliberately not a dependency - it filters
     `descendants()` below and is not an input to the layout. */
  const treemapRootNode = useMemo(
    () => layout([width, height], tilingMethod)(buildHierarchy(sizedNodes)),
    [sizedNodes, width, height, tilingMethod]
  );
  /* Memoized alongside the layout, not merely derived from it. A fresh array every render makes
     every memo keyed on it inside `useHierarchyNavigation` dead - the positions would be rebuilt
     and, worse, `toNodeProps` would be a new function, so React would detach and re-attach every
     tile's ref on every render. `useResizeObserver` fires per frame of a splitter drag, which is
     why the layout above is memoized at all. */
  const drawnNodes = useMemo(
    () => toDrawnOrder(treemapRootNode, maxDepth),
    [treemapRootNode, maxDepth]
  );
  /* A tile is a group only where its children are on screen. One whose children the depth
     setting cut away is a leaf as far as a reader is concerned, and drawing it as a group gives
     an empty grey frame with a one-line stripe where its own colour and label should be.
     `HierarchicalPackingVisualization` decides the same thing the same way. */
  const drawn = new Set(drawnNodes.map(({ data }) => data.id));

  const navigableNodes = useMemo(
    () => toNavigableNodes(drawnNodes),
    [drawnNodes]
  );
  const { treeProps, toNodeProps } = useHierarchyNavigation(
    navigableNodes,
    t(
      i18nLocale,
      'tabview:hierarchicalTreemap.treemapTreeLabel',
      'Hierarchical treemap'
    )
  );

  return (
    <TreemapContainer
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
      ref={viewContainer}
      {...treeProps}
    >
      {drawnNodes.map((node, index) => (
        <HierarchicalTreemapViewNode
          key={node.data.id}
          node={node}
          hasDrawnChildren={Boolean(
            node.children?.some(child => drawn.has(child.data.id))
          )}
          formatValue={formatValue}
          a11yProps={toNodeProps(
            navigableNodes[index],
            toAccessibleName(node.data.label, formatValue(node.value))
          )}
        />
      ))}
    </TreemapContainer>
  );
};
